import React from 'react';
import { useTranslation } from 'react-i18next';
import PageBody from '@lib/layouts/DashboardLayout/PageBody/PageBody';
import WhiteBox from '@lib/components/WhiteBox/WhiteBox';
import DashboardTitleHeader from '@lib/layouts/DashboardLayout/DashboardTitleHeader/DashboardTitleHeader';
import IconButton from '@lib/components/IconButton/IconButton';
import Button from '@lib/components/Button/Button';
import Table from '@lib/components/Table/Table';
import ListEmptyState from '@lib/components/ListEmptyState/ListEmptyState';
import Trans from '@lib/components/Trans/Trans';
import {
  Modals,
  UseModalManagementResult,
} from '@lib/hooks/useModalManagement';
import DashboardContentMaxWidth from '@lib/layouts/DashboardLayout/DashboardContentMaxWidth/DashboardContentMaxWidth';
import Tooltip from '@lib/components/Tooltip/Tooltip';
import getTableActionsMenu from '../../utils/getTableActionsMenu';
import getTableColumns from '../../utils/getTableColumns';
import { UseActionsResult } from '../../utils/useActions';

interface Props {
  useActionsResult: UseActionsResult;
  useModalManagementResult: UseModalManagementResult;
}

function ApiTokenList(props: Props) {
  const { useModalManagementResult, useActionsResult } = props;
  const { t } = useTranslation();
  const { openModal } = useModalManagementResult;

  const {
    apiTokensQuery,
    isCreateApiTokenAvailable,
    onDestroyApiToken,
    onOpenEditApiTokenModal,
    onStatusChangeHandler,
  } = useActionsResult;

  const tableColumns = getTableColumns({ onStatusChangeHandler, t });
  const tableActionsMenu = getTableActionsMenu({
    t,
    onEdit: onOpenEditApiTokenModal,
    onDelete: onDestroyApiToken,
  });
  const tableEmptyState = (
    <ListEmptyState
      title={<Trans i18nKey="no-api-token-yet" />}
      text={<Trans i18nKey="no-api-token-text" />}
      actions={[
        isCreateApiTokenAvailable ? (
          <Button
            key="add"
            buttonText={t('add-api-token')}
            leftIcon="add"
            onClick={() => openModal(Modals.ApiTokenFormModal)}
          />
        ) : (
          <Tooltip body={t('add-api-token-disabled-desc')} key="add-disabled">
            <Button buttonText={t('add-api-token')} leftIcon="add" disabled />
          </Tooltip>
        ),
      ]}
    />
  );
  return (
    <PageBody withoutHeader>
      <DashboardContentMaxWidth>
        <WhiteBox>
          <DashboardTitleHeader
            title={t('public-api')}
            actions={[
              <IconButton
                key="help"
                icon="help"
                symbolsOutlined
                onClick={() => openModal(Modals.HowToUseApi)}
              />,
              isCreateApiTokenAvailable ? (
                <Button
                  key="add"
                  buttonText={t('add-api-token')}
                  leftIcon="add"
                  onClick={() => openModal(Modals.ApiTokenFormModal)}
                />
              ) : (
                <Tooltip
                  body={t('add-api-token-disabled-desc')}
                  key="add-disabled"
                >
                  <Button
                    buttonText={t('add-api-token')}
                    leftIcon="add"
                    disabled
                  />
                </Tooltip>
              ),
            ]}
          />
          <Table
            id="token"
            columns={tableColumns}
            data={apiTokensQuery.collection}
            totalEntries={apiTokensQuery.totalCount}
            emptyState={tableEmptyState}
            loading={apiTokensQuery.loading}
            actionsMenu={tableActionsMenu}
          />
        </WhiteBox>
      </DashboardContentMaxWidth>
    </PageBody>
  );
}

export default ApiTokenList;
